import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, ShoppingCart, Monitor, Package, BarChart3, Users, Menu as MenuIcon, UtensilsCrossed, Settings, Wallet, ClipboardList, LayoutGrid, Bell, Landmark, Bike, History, CalendarClock, PanelLeftClose, PanelLeft, Tablet, Coins, Scale, Contact, BadgeCheck, Vault, Building2, Lock, FileText } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { LOGO } from "../../lib/api";
import { PrintStationAlert } from "../PrintStationAlert";
import { ConnectionStatus } from "../ConnectionStatus";
import { VersionsBell } from "../VersionsBell";
import { startSyncManager } from "../../lib/offlineQueue";
import { modulesForUser, planLocked } from "../../lib/modules";
import { VoiceSettings } from "../VoiceSettings";
import { QzStatus } from "../QzStatus";
import { TransferAlerts } from "../TransferAlerts";
import { toast } from "sonner";

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    o.start(); o.stop(ctx.currentTime + 0.5);
  } catch (e) { /* ignore */ }
}

const ROLE_LABELS = {
  superadmin: "Superadmin",
  admin: "Administrador",
  manager: "Gerente",
  waiter: "Mesero",
  cashier: "Cajero",
  kitchen: "Cocina",
};

const NAV = [
  { area: "Operación", key: "inicio", to: "/app", label: "Inicio", icon: LayoutDashboard, roles: ["admin", "manager", "cashier"] },
  { area: "Operación", key: "mesas", to: "/app/mesas", label: "Mesas", icon: LayoutGrid, roles: ["waiter"] },
  { area: "Operación", key: "comandera", to: "/app/comandera", label: "Comandera", icon: Tablet, roles: ["admin", "manager", "cashier", "waiter"] },
  { area: "Operación", key: "pedido", to: "/app/pedido", label: "Generar Pedido", icon: ShoppingCart, roles: ["admin", "manager", "cashier", "waiter"] },
  { area: "Operación", key: "pedidos", to: "/app/pedidos", label: "Pedidos", icon: ClipboardList, roles: ["admin", "manager", "cashier", "waiter"] },
  { area: "Operación", key: "tablet", to: "/app/tablet", label: "Modo Tablet", icon: Tablet, roles: ["admin", "manager"] },
  { area: "Cocina", key: "kds", to: "/app/kds", label: "Cocina (KDS)", icon: Monitor, roles: ["admin", "manager", "kitchen"] },
  { area: "Caja y finanzas", key: "caja", to: "/app/caja", label: "Caja", icon: Wallet, roles: ["admin", "manager", "cashier"] },
  { area: "Caja y finanzas", key: "cajas", to: "/app/cajas", label: "Cajas de cajeros", icon: Vault, roles: ["admin", "manager"] },
  { area: "Caja y finanzas", key: "bancos", to: "/app/bancos", label: "Bancos y Cajas", icon: Landmark, roles: ["admin", "manager", "cashier"] },
  { area: "Caja y finanzas", key: "historial", to: "/app/historial", label: "Historial", icon: History, roles: ["admin", "manager", "cashier"] },
  { area: "Caja y finanzas", key: "facturacion", to: "/app/facturacion", label: "Facturación", icon: FileText, roles: ["admin", "manager", "cashier"] },
  { area: "Caja y finanzas", key: "propinas", to: "/app/propinas", label: "Propinas", icon: Coins, roles: ["admin", "manager"] },
  { area: "Caja y finanzas", key: "propinas-pendientes", to: "/app/propinas-pendientes", label: "Propinas pendientes", icon: Coins, roles: ["admin", "manager", "waiter", "cashier"] },
  { area: "Caja y finanzas", key: "gastos", to: "/app/gastos", label: "Gastos", icon: Wallet, roles: ["admin", "manager"] },
  { area: "Caja y finanzas", key: "gastos-fijos", to: "/app/gastos-fijos", label: "Gastos Fijos", icon: CalendarClock, roles: ["admin", "manager"] },
  { area: "Inventario", key: "productos", to: "/app/productos", label: "Productos", icon: UtensilsCrossed, roles: ["admin", "manager"] },
  { area: "Inventario", key: "inventario", to: "/app/inventario", label: "Inventario", icon: Package, roles: ["admin", "manager"] },
  { area: "Clientes y ventas en línea", key: "clientes", to: "/app/clientes", label: "Clientes", icon: Contact, roles: ["admin", "manager", "cashier"] },
  { area: "Clientes y ventas en línea", key: "repartidores", to: "/app/repartidores", label: "Control de Repartidores", icon: Bike, roles: ["admin", "manager", "cashier"] },
  { area: "Clientes y ventas en línea", key: "conciliacion", to: "/app/conciliacion", label: "Conciliación Plataformas", icon: Scale, roles: ["admin", "manager"] },
  { area: "Reportes", key: "reportes", to: "/app/reportes", label: "Reportes", icon: BarChart3, roles: ["admin", "manager"] },
  { area: "Administración", key: "usuarios", to: "/app/usuarios", label: "Usuarios", icon: Users, roles: ["admin", "manager"] },
  { area: "Administración", key: "nomina", to: "/app/nomina", label: "Nómina", icon: Users, roles: ["admin", "manager"] },
  { area: "Administración", key: "multisucursal", to: "/app/negocios", label: "Mis Negocios", icon: Building2, roles: ["admin", "manager"] },
  { area: "Administración", key: "configuracion", to: "/app/configuracion", label: "Configuración", icon: Settings, roles: ["admin", "manager"] },
  { area: "Administración", key: "suscripcion", to: "/app/suscripcion", label: "Mi Suscripción", icon: BadgeCheck, roles: ["admin"] },
];
const AREAS = [...new Set(NAV.map((n) => n.area))];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();

  useEffect(() => { startSyncManager(); }, []);

  // Autoconecta QZ Tray al cargar para estaciones de restaurante (detección "por defecto").
  useEffect(() => {
    if (!user || !["admin", "manager", "cashier", "waiter", "kitchen"].includes(user.role)) return;
    import("../../lib/qz").then(({ startQzKeepAlive }) => startQzKeepAlive());
  }, [user]);

  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [settings, setSettings] = useState({});
  const [incoming, setIncoming] = useState([]);
  const [muted, setMuted] = useState(false);
  const [dueFixed, setDueFixed] = useState(0);
  const lastTopRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const fetchSettings = () => api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
    fetchSettings();
    window.addEventListener("settings-updated", fetchSettings);
    return () => window.removeEventListener("settings-updated", fetchSettings);
  }, [user]);

  // Pedidos de Página Web pendientes de ACEPTAR (aviso global, un pedido a la vez)
  useEffect(() => {
    if (!user || !["admin", "manager", "cashier", "waiter"].includes(user.role)) return;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/orders/incoming");
        if (!alive) return;
        const list = data || [];
        setIncoming(list);
        const topId = list[0]?.id || null;
        if (topId && topId !== lastTopRef.current) setMuted(false);
        lastTopRef.current = topId;
      } catch (e) { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  // Sonido continuo mientras haya un pedido por aceptar (hasta silenciar)
  useEffect(() => {
    if (incoming.length === 0 || muted) return;
    beep();
    const t = setInterval(beep, 3500);
    return () => clearInterval(t);
  }, [incoming.length, muted]);

  // Las comandas de pedidos web (incluido pago con tarjeta) las imprime la estación de impresión.

  // Aviso de gastos fijos por vencer (solo admin/gerente)
  useEffect(() => {
    if (!user || !["admin", "manager"].includes(user.role)) return;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/fixed-expenses/due");
        if (alive) setDueFixed(data?.count || 0);
      } catch (e) { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 60000);
    window.addEventListener("fixed-expense-changed", poll);
    return () => { alive = false; clearInterval(t); window.removeEventListener("fixed-expense-changed", poll); };
  }, [user]);

  const allowedModules = modulesForUser(user);
  const items = NAV.filter(
    (n) =>
      n.roles.includes(user?.role) &&
      (allowedModules === null || allowedModules.includes(n.key) || planLocked(user, n.key)) &&
      !(n.to === "/app/kds" && settings.kds_enabled === false) &&
      !(n.to === "/app/propinas" && settings.tips_enabled === false) &&
      !(n.to === "/app/propinas-pendientes" && settings.tips_enabled === false)
  );

  const canSeeTransfers = items.some((n) => n.key === "multisucursal") && !planLocked(user, "multisucursal");

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="h-screen flex bg-[#F8FAFC] overflow-hidden">
      {canSeeTransfers && <TransferAlerts user={user} />}
      {incoming.length > 0 && (() => {
        const o = incoming[0];
        const total = (o.subtotal || 0).toFixed(2);
        const tipo = o.fulfillment === "recoger" ? "Para recoger" : "A domicilio";
        return (
          <div className="fixed bottom-4 right-4 z-[60] w-[92%] max-w-md" data-testid="web-order-alert">
            <div className="bg-[#7C3AED] text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3 animate-pulse">
              <span className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><Bell size={22} /></span>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-extrabold leading-tight">Nuevo pedido de Página Web</p>
                <p className="text-white/90 text-sm truncate">{o.customer_name || "Cliente"} · {tipo} · ${total}</p>
                {incoming.length > 1 && <p className="text-white/60 text-xs mt-0.5">+{incoming.length - 1} más por aceptar</p>}
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button data-testid="web-order-alert-view" onClick={() => { setMuted(true); navigate("/app/pedidos"); }}
                  className="px-3 py-2 rounded-lg bg-white text-[#7C3AED] text-sm font-bold hover:bg-white/90">Ver pedido</button>
                <button data-testid="web-order-alert-mute" onClick={() => setMuted(true)}
                  className="px-3 py-1.5 rounded-lg bg-white/15 text-white text-sm font-bold hover:bg-white/25">Silenciar</button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[#E85D04] text-white flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${collapsed ? "lg:hidden" : ""}`}
      >
        <div className="p-4 border-b border-white/10">
          <div className="relative bg-white rounded-2xl px-4 py-3 shadow-sm">
            <img src={LOGO} alt="ProfesionalPosRestaurante" className="w-40 mx-auto" />
            <button data-testid="sidebar-collapse" onClick={() => setCollapsed(true)} title="Ocultar menú"
              className="hidden lg:flex absolute top-1/2 -translate-y-1/2 right-2 w-8 h-8 rounded-lg bg-[#E85D04]/10 text-[#E85D04] hover:bg-[#E85D04]/20 items-center justify-center">
              <PanelLeftClose size={18} />
            </button>
          </div>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          {AREAS.map((area) => {
            const group = items.filter((n) => n.area === area);
            if (!group.length) return null;
            return (
              <div key={area} className="mb-3" data-testid={`nav-area-${area.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/55">{area}</p>
                <div className="space-y-0.5">
          {group.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/app"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1F2937] text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <n.icon size={18} />
              <span className="flex-1">{n.label}</span>
              {planLocked(user, n.key) && <Lock size={14} className="text-white/60" data-testid={`nav-lock-${n.key}`} />}
              {n.to === "/app/gastos-fijos" && dueFixed > 0 && (
                <span data-testid="nav-due-badge" className="min-w-5 h-5 px-1.5 rounded-full bg-[#DC2626] text-white text-[11px] font-bold flex items-center justify-center">{dueFixed}</span>
              )}
            </NavLink>
          ))}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="mb-2"><VersionsBell variant="sidebar" /></div>
          <div className="mb-2"><VoiceSettings variant="sidebar" /></div>
          <div className="mb-2"><QzStatus /></div>
          <div className="px-2 mb-3 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{user?.name}</p>
              <p className="text-xs text-white/50">{ROLE_LABELS[user?.role]}</p>
              {user?.home_restaurant_id && (
                <p data-testid="sidebar-branch-chip" className="mt-1 text-[11px] font-bold bg-white/15 rounded-md px-2 py-0.5 truncate">Sucursal: {user.restaurant_name}</p>
              )}
            </div>
            <ConnectionStatus compact />
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white/80 hover:bg-[#DC2626] hover:text-white transition-colors"
          >
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0 h-screen">
        {collapsed && (
          <button data-testid="sidebar-expand" onClick={() => setCollapsed(false)} title="Mostrar menú"
            className="hidden lg:flex fixed top-4 left-4 z-30 w-10 h-10 rounded-xl bg-[#E85D04] text-white shadow-lg items-center justify-center hover:bg-[#C2410C]">
            <PanelLeft size={20} />
          </button>
        )}
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-[#111827]/10 px-4 h-14 flex items-center gap-3 shrink-0">
          <button data-testid="menu-toggle" onClick={() => setOpen(true)} className="p-2">
            <MenuIcon size={22} className="text-[#E85D04]" />
          </button>
          <img src={LOGO} alt="logo" className="h-8" />
          <div className="ml-auto flex items-center gap-1">
            <VoiceSettings variant="header" />
            <VersionsBell variant="header" />
            <ConnectionStatus compact />
          </div>
        </header>
        <PrintStationAlert user={user} />
        <main className="flex-1 min-w-0 overflow-y-auto" data-testid="app-main-scroll">{children}</main>
      </div>
    </div>
  );
}
